/**
 * Image Library Service
 * Serviço para gerenciar a biblioteca de imagens do usuário
 */

import { api } from '../lib/api';

export const imageLibraryService = {
  /**
   * Upload de imagem para biblioteca
   * @param {File} file - Arquivo de imagem
   * @returns {Promise<{success: boolean, data: ImageLibraryItem, message: string}>}
   */
  async uploadImage(file) {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post('/api/user/brand-identity/library/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });

    return response.data;
  },

  /**
   * Listar todas as imagens da biblioteca
   * @returns {Promise<{success: boolean, data: ImageLibraryItem[], total: number, max_allowed: number}>}
   */
  async listImages() {
    const response = await api.get('/api/user/brand-identity/library');
    return response.data;
  },

  /**
   * Atualizar caption de uma imagem
   * @param {string} imageId - ID da imagem
   * @param {string} caption - Nova descrição (máx 200 caracteres)
   * @returns {Promise<{success: boolean, data: ImageLibraryItem, message: string}>}
   */
  async updateCaption(imageId, caption) {
    const response = await api.put(`/api/user/brand-identity/library/${imageId}`, {
      caption
    });
    return response.data;
  },

  /**
   * Remover imagem da biblioteca
   * @param {string} imageId - ID da imagem
   * @returns {Promise<{success: boolean, message: string}>}
   */
  async deleteImage(imageId) {
    const response = await api.delete(`/api/user/brand-identity/library/${imageId}`);
    return response.data;
  },

  /**
   * Helpers
   */
  helpers: {
    /**
     * Valida se o arquivo é uma imagem válida
     */
    isValidImage(file) {
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
      return allowedTypes.includes(file.type);
    },

    /**
     * Valida tamanho do arquivo (máx 5MB)
     */
    isValidSize(file) {
      const maxSize = 5 * 1024 * 1024; // 5MB
      return file.size <= maxSize;
    },

    /**
     * Formata tamanho do arquivo para exibição
     */
    formatFileSize(bytes) {
      if (bytes < 1024) return `${bytes} B`;
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    },

    /**
     * Verifica se pode adicionar mais imagens
     */
    canAddMore(currentCount, maxAllowed = 20) {
      return currentCount < maxAllowed;
    }
  }
};

/**
 * @typedef {Object} ImageLibraryItem
 * @property {string} id - ID único da imagem
 * @property {string} image_url - URL da imagem no storage
 * @property {string} caption - Descrição da imagem (máx 200 caracteres)
 * @property {string} created_at - Data de criação (ISO format)
 */

export default imageLibraryService;